/**
 * HubNavShell — replaces the old hard-coded header div in
 * LibraryHubSlideOver with a structured navigation chrome.
 *
 * Renders:
 *   Left   — back button (when back !== null, kind = "back") or
 *             cancel button (kind = "cancel"). Hidden when no back
 *             target exists.
 *   Centre — HubBreadcrumb trail + signed badge.
 *   Right  — close × button.
 *
 * Does NOT own any state. All data flows in as props from the
 * parent shell. The back-button click calls onBack(); the close
 * button calls onClose(). Both callbacks come from useHubNavigation
 * or the shell directly.
 *
 * Signed badge semantics (preserved from original header):
 *   • Only shown in "list" or "detail" modes when library !== null.
 *   • Green pill: library.signed === true.
 *   • Amber pill: pending count (library.unverified ?? 0).
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import type { Library } from "../../../types";
import type { BackTarget } from "../hooks/useHubNavigation";
import { brandColors, elevation, fonts, inkColors, statusColors, surfaceColors } from "../tokens";
import { HubBreadcrumb } from "./HubBreadcrumb";
import type { HubMode } from "./LibraryHubSlideOver";

export interface HubNavShellProps {
  mode: HubMode;
  workspaceName: string;
  library: Library | null;
  /** Currently selected doc filename — drives the detail breadcrumb. */
  docFilename?: string | null;
  back: BackTarget | null;
  closeBtnRef: React.RefObject<HTMLButtonElement>;
  onBack: () => void;
  onClose: () => void;
}

const useStyles = makeStyles({
  header: {
    display: "flex",
    alignItems: "center",
    columnGap: "6px",
    padding: "10px 12px 10px 10px",
    borderBottom: `1px solid ${surfaceColors.line}`,
    backgroundColor: surfaceColors.paper,
    flexShrink: 0,
    minHeight: "48px",
    // Subtle bottom shadow so the header lifts visually above the
    // scrollable content area without a heavy border.
    boxShadow: "0 1px 0 0 rgba(15,10,30,0.05)",
  },
  // Back / cancel affordance — shown only when back !== null.
  backBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    padding: "0",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    color: inkColors.ink3,
    flexShrink: 0,
    ":hover": {
      backgroundColor: brandColors.light,
      color: brandColors.primary,
    },
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "2px",
    },
  },
  backChevron: {
    fontSize: "20px",
    lineHeight: 1,
  },
  // Centre region: breadcrumb + badge.
  centre: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    columnGap: "6px",
    minWidth: 0,
    overflow: "hidden",
  },
  // Signed / pending badge — preserved from original header.
  signedBadge: {
    fontSize: "8.5px",
    fontWeight: 700,
    fontFamily: fonts.mono,
    padding: "2px 6px",
    borderRadius: "9999px",
    letterSpacing: "0.3px",
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  signedYes: {
    backgroundColor: "var(--success-soft)",
    color: "var(--success)",
    border: `1px solid ${statusColors.cool}`,
  },
  signedNo: {
    backgroundColor: "var(--warning-soft)",
    color: "var(--warning)",
    border: `1px solid ${statusColors.warn}`,
  },
  // Close ×.
  closeBtn: {
    width: "24px",
    height: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "15px",
    color: inkColors.ink3,
    flexShrink: 0,
    lineHeight: 1,
    fontFamily: fonts.body,
    ":hover": {
      backgroundColor: surfaceColors.paperDim,
      color: inkColors.ink,
    },
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "2px",
    },
  },
});

function shouldShowSignedBadge(mode: HubMode): boolean {
  return mode === "list" || mode === "detail";
}

export const HubNavShell: React.FC<HubNavShellProps> = ({
  mode,
  workspaceName,
  library,
  docFilename,
  back,
  closeBtnRef,
  onBack,
  onClose,
}) => {
  const styles = useStyles();

  const showBadge = shouldShowSignedBadge(mode) && library !== null;

  return (
    <div className={styles.header} data-testid="hub-nav-shell">
      {/* ── Left: back / cancel ── */}
      {back && (
        <button
          type="button"
          className={styles.backBtn}
          aria-label={back.ariaLabel}
          data-testid="hub-back"
          onClick={onBack}
          title={back.label}
        >
          {back.kind === "back" && (
            <span className={styles.backChevron} aria-hidden="true">
              ‹
            </span>
          )}
        </button>
      )}

      {/* ── Centre: breadcrumb + badge ── */}
      <div className={styles.centre}>
        <HubBreadcrumb
          mode={mode}
          workspaceName={workspaceName}
          libraryName={library?.name}
          docFilename={docFilename}
        />
        {showBadge && library?.signed && (
          <span
            className={mergeClasses(styles.signedBadge, styles.signedYes)}
            aria-label={`signed ${library.signed_by ?? ""}`.trim()}
          >
            {`signed${library.signed_by ? ` · ${library.signed_by}` : ""}`}
          </span>
        )}
      </div>

      {/* ── Right: close ── */}
      <button
        ref={closeBtnRef}
        type="button"
        className={styles.closeBtn}
        onClick={onClose}
        aria-label="Close library hub"
        data-testid="hub-close"
      >
        ×
      </button>
    </div>
  );
};

export default HubNavShell;
